import React, { PureComponent } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { MultiMediaForm } from '../Forms'
import * as ContentActions from '../../../actions/content'
import { Types as MediaTypes } from '../../../actions/content/media'
import { notify as notifyAction } from '../../../actions/notifications'
import { Modal, Padding } from '../../../components'
import MediaDOM from '../../../components/Screens/Content/Media'
import { ACL, colors, Routes } from '../../../constants'
import { Selectors as MediaSelectors } from '../../../reducers/content/media'
import { EstateUtils, MediaUtils } from '../../../utils'
import { withSize } from '../../../components/utils'

class Media extends PureComponent {
    static propTypes = {
        countPendingApproval: PropTypes.number.isRequired,
        countPendingPush: PropTypes.number.isRequired,
        countPendingTest: PropTypes.number.isRequired,
        data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
        fetchMedia: PropTypes.func.isRequired,
        fetchSingleMedia: PropTypes.func.isRequired,
        hasAdCreateAccess: PropTypes.bool.isRequired,
        isDownloadBlocked: PropTypes.bool.isRequired,
        history: PropTypes.shape({
            push: PropTypes.func,
        }).isRequired,
        isWorking: PropTypes.bool.isRequired,
        pagination: PropTypes.shape({
            currentPage: PropTypes.number,
            to: PropTypes.number,
            total: PropTypes.number,
        }).isRequired,
        deleteMedia: PropTypes.func.isRequired,
        changeMediaArray: PropTypes.func.isRequired,
        downloadMedia: PropTypes.func.isRequired,
        retryUploadMedia: PropTypes.func.isRequired,
        selectedEstate: PropTypes.string.isRequired,
    }

    constructor(props) {
        super(props)

        this.state = {
            search: '',
            isPendingApprovFilterEnabled: false,
            isPendingPushFilterEnabled: false,
            isPendingTestFilterEnabled: false,
            isFormMediaOpen: false,
            isVideoPlayerOpen: false,
            videoPlayerUrl: null,
            tagSearch: '',
        }

        this.timeouts = {}
    }

    componentDidMount() {
        this.loadData()
    }

    componentDidUpdate(lastProps) {
        const { data: lastData, selectedEstate: lastEstate } = lastProps
        const { data: nextData, selectedEstate: nextEstate } = this.props
        if (lastEstate !== nextEstate) {
            this.loadData()
        }

        // If media data changes
        if (lastData !== nextData) {
            this.checkMediaItems()
        }
    }

    checkMediaItems = () => {
        const { data, fetchSingleMedia } = this.props

        // For every media in the data array
        data.forEach((media) => {
            const { id } = media

            // If it's not already in the interval, and the media is being processed,
            // but not for too long
            if (!this.timeouts[id] && MediaUtils.isBeingUploaded(media) && !MediaUtils.hasFailedUpload(media)) {
                // Creates a new timeout to fetch fetch again the information
                this.timeouts[id] = setTimeout(() => {
                    // We delete the current timeout, because if it fetches again, and the
                    // continues being unprocessed, it will enter this if again
                    delete this.timeouts[id]

                    // No need to await the fetch, just trigger it
                    fetchSingleMedia(id)
                }, 20000)
            }
        })
    }

    /**
     * On open media form
     * @param {Number} media Media ID
     */
    onInspectMedia = async (media) => {
        const { history, selectedEstate } = this.props
        history.push(
            Routes.buildRoute(Routes['CONTENT:MEDIA:DETAILS'], {
                estate: selectedEstate,
                media,
            })
        )
    }

    /**
     * On open media form
     * @param {Number} mediaID
     */
    onEditMedia = async (mediaID) => {
        const { data: media, history } = this.props

        if (!media) {
            return null
        }

        const selectedMedia = media.find(({ id }) => id === mediaID)

        if (!selectedMedia) {
            return null
        }

        const { render_tool_id: project } = selectedMedia

        if (!project) {
            return null
        }

        history.push(Routes.buildRoute(Routes.RENDER, { project }))
    }

    /**
     * On open media form
     */
    onOpenFormMedia = () => {
        this.setState({ isFormMediaOpen: true })
    }

    /**
     * On close media form
     */
    onClickCancel = () => {
        this.setState({ isFormMediaOpen: false })
    }

    /**
     * On remove media
     * @param {Number} mediaID
     */
    onDeleteMedia = (mediaID) => {
        const { data, deleteMedia } = this.props

        if (mediaID) {
            deleteMedia(mediaID)
        } else {
            const mediaToDelete = data.filter((m) => m.selected)
            mediaToDelete.forEach((m) => {
                deleteMedia(m.id)
            })
        }
    }

    /**
     * Once all the media have been upload to the server, and crated the record,
     * we attach them to the playlist
     * @param {Array} storedMediaList Redux action from upload
     * @async
     */
    onFinishSubmitMedia = async () => {
        this.setState({ isFormMediaOpen: false })
    }

    onDownloadMedia = async (mediaID, videoPlayer = false) => {
        const { data: media, isDownloadBlocked } = this.props

        if (isDownloadBlocked && !videoPlayer) {
            return null
        }

        if (!media || !media.length) {
            return null
        }

        const { downloadMedia } = this.props
        const selectedMedia = media.find(({ id }) => id === mediaID)
        const response = await downloadMedia(mediaID, videoPlayer)
        const { name } = selectedMedia

        if (response.type !== MediaTypes.DOWNLOAD_MEDIA_SUCCESS) {
            return null
        }

        const url = response.payload
        if (!videoPlayer) {
            const downloadTag = document.createElement('a')
            downloadTag.href = url
            downloadTag.target = '_blank'
            downloadTag.download = name
            downloadTag.click()
            return
        }
        this.setState({ isVideoPlayerOpen: true, videoPlayerUrl: url })
    }

    onRetryUpload = async (mediaID) => {
        const { retryUploadMedia } = this.props
        await retryUploadMedia(mediaID)
    }

    /**
     * Tag media as selected, or unselected (the oposite to the last value)
     * @param {Number} mediaID
     */
    bulkSelectionMedia = (mediaID) => {
        const { data, changeMediaArray } = this.props

        // Data is not present
        if (!data) {
            return null
        }

        // Get index of selected media
        const selectedMediaIndex = data.findIndex(({ id }) => id === mediaID)

        // Media not found
        if (selectedMediaIndex === -1) {
            return null
        }

        // Selected media
        const selectedMedia = data[selectedMediaIndex]

        // Updated media array
        const newMediaArray = [
            ...data.slice(0, selectedMediaIndex),
            {
                ...selectedMedia,
                selected: !selectedMedia.selected,
            },
            ...data.slice(selectedMediaIndex + 1, data.length),
        ]

        // Perform the update
        changeMediaArray(newMediaArray)
    }

    // On Change any input value
    onChangeSearch = ({ target: { value } }) => {
        this.setState({ search: value }, this.search)
    }

    /**
     * Toggle the value of `isPendingApprovFilterEnabled` state
     */
    onClickPendingApprovalFilter = () => {
        const { isPendingApprovFilterEnabled } = this.state
        const newValue = !isPendingApprovFilterEnabled
        const newState = {
            isPendingApprovFilterEnabled: newValue,
        }

        if (newValue) {
            newState.isPendingPushFilterEnabled = false
        }

        this.setState(newState, () => {
            this.loadData()
        })
    }

    /**
     * Toggle the value of `isPendingPushFilterEnabled` state
     */
    onClickPendingPushFilter = () => {
        const { isPendingPushFilterEnabled } = this.state

        const newValue = !isPendingPushFilterEnabled

        const newState = {
            isPendingPushFilterEnabled: newValue,
        }

        if (newValue) {
            newState.isPendingApprovFilterEnabled = false
            newState.isPendingTestFilterEnabled = false
        }

        this.setState(newState, () => {
            this.loadData()
        })
    }

    /**
     * Toggle the value of `isPendingPushFilterEnabled` state
     */
    onClickPendingTestFilter = () => {
        const { isPendingTestFilterEnabled } = this.state
        const newValue = !isPendingTestFilterEnabled
        const newState = {
            isPendingTestFilterEnabled: newValue,
        }

        if (newValue) {
            newState.isPendingPushFilterEnabled = false
        }

        this.setState(newState, () => {
            this.loadData()
        })
    }

    onRenderButtonClick = () => {
        const { history } = this.props
        history.push(Routes.buildRoute(Routes.RENDER))
    }

    buildNextPageFunc() {
        const { fetchMedia, isWorking, pagination } = this.props
        const { currentPage, to, total } = pagination
        const { search, tagSearch } = this.state

        if (!isWorking && to < total) {
            return () => fetchMedia(currentPage + 1, { name: search, tag: tagSearch })
        }

        return null
    }

    buildOnClickRender = () => {
        const { hasAdCreateAccess } = this.props
        const onRenderButtonClick = () => {
            const { history } = this.props
            history.push(Routes.buildRoute(Routes.RENDER))
        }

        return hasAdCreateAccess ? onRenderButtonClick : null
    }

    async loadData() {
        const { fetchMedia } = this.props
        const {
            isPendingApprovFilterEnabled: pendingApproval,
            isPendingPushFilterEnabled: pendingPush,
            isPendingTestFilterEnabled: pendingTest,
            search,
            tagSearch,
        } = this.state

        await fetchMedia(0, {
            name: search,
            pendingApproval,
            pendingPush,
            pendingTest,
            tag: tagSearch,
        })
    }

    search() {
        const { search } = this.state

        if (search.length === 0 || search.length > 2) {
            this.loadData()
        }
    }

    tagSearch() {
        const { tagSearch } = this.state

        if (tagSearch.length === 0 || tagSearch.length > 2) {
            this.loadData()
        }
    }

    renderVideoPlayer() {
        const { isVideoPlayerOpen, videoPlayerUrl } = this.state
        const { isMobile } = this.props

        return (
            <Modal isOpen={isVideoPlayerOpen} onClose={() => this.setState({ isVideoPlayerOpen: false })} minWidth="50%">
                <Padding size="ssm">
                    <video
                        controls
                        controlsList="nodownload"
                        autoPlay
                        disablePictureInPicture
                        onContextMenu={(e) => e.preventDefault()}
                        width="auto"
                        style={{ height: isMobile ? 'auto' : '60vh', maxWidth: '85vw' }}
                    >
                        <track kind="captions" />
                        <source src={videoPlayerUrl} />
                    </video>
                </Padding>
            </Modal>
        )
    }

    render() {
        const { countPendingApproval, countPendingPush, countPendingTest, data, isDownloadBlocked, isWorking } = this.props
        const { isFormMediaOpen, isPendingApprovFilterEnabled, isPendingPushFilterEnabled, isPendingTestFilterEnabled, search } = this.state
        return (
            <MediaDOM
                backGroundColor={colors.greylighter}
                bulkSelection={this.bulkSelectionMedia}
                countPendingApproval={countPendingApproval}
                countPendingPush={countPendingPush}
                countPendingTest={countPendingTest}
                data={data}
                isDownloadBlocked={isDownloadBlocked}
                isLoading={isWorking}
                isPendingApprovFilterEnabled={isPendingApprovFilterEnabled}
                isPendingPushFilterEnabled={isPendingPushFilterEnabled}
                isPendingTestFilterEnabled={isPendingTestFilterEnabled}
                nextPage={this.buildNextPageFunc()}
                onChangeSearch={this.onChangeSearch}
                onClickPendingApprovalFilter={this.onClickPendingApprovalFilter}
                onClickPendingPushFilter={this.onClickPendingPushFilter}
                onClickPendingTestFilter={this.onClickPendingTestFilter}
                onClickUpload={this.onClickUpload}
                onClickUrl={this.onClickUrl}
                onDownloadMedia={this.onDownloadMedia}
                onEditMedia={this.onEditMedia}
                onInspectMedia={this.onInspectMedia}
                onOpenFormMedia={this.onOpenFormMedia}
                onRemoveMedia={this.onDeleteMedia}
                onRenderButtonClick={this.buildOnClickRender()}
                onRetryUpload={this.onRetryUpload}
                twoColumns
                valueSearch={search}
            >
                {this.renderVideoPlayer()}
                <MultiMediaForm isOpen={isFormMediaOpen} onClickCancel={this.onClickCancel} onFinishSubmit={this.onFinishSubmitMedia} />
            </MediaDOM>
        )
    }
}

const mapStateToProps = (state) => {
    const {
        app,
        app: { selectedEstate },
        content: {
            isWorking: { media: isWorking },
            media: data,
            pagination: {
                media: { currentPage, to, total },
            },
        },
    } = state
    return {
        countPendingApproval: MediaSelectors.mediaPendingApprovalCount(state),
        countPendingPush: MediaSelectors.mediaPendingPushCount(state),
        countPendingTest: MediaSelectors.mediaPendingTestCount(state),
        selectedEstate,
        data,
        isWorking,
        pagination: {
            currentPage,
            to,
            total,
        },
        hasAdCreateAccess: EstateUtils.hasPermission({ app }, { acls: [ACL.VIEW_ADCREATE] }, { ignoreAdmin: true }),
        isDownloadBlocked: EstateUtils.checkACL({ app }, ACL.DOWNLOAD_BLOCKED),
    }
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            ...ContentActions,
            fetchSingleMedia: ContentActions.fetchSingleMedia,
            notify: notifyAction,
        },
        dispatch
    )

export default connect(mapStateToProps, mapDispatchToProps)(withSize(Media))
